import { SchedulePayment } from '@aaa-ncnu-ie/mypolicy-api-billing';
import { BillingDetails } from '@aaa-ncnu-ie/mypolicy-api-customers';
import { Card, Stack, Typography } from '@mui/material';

import { usePoliciesQuery } from '../../../api/getPolicies';
import AutoPayIcon from '../../../assets/icons/AutoPay';
import CardHeader from '../../../components/CardHeader';
import Link from '../../../components/styledComponents/Link';
import StatusAlert from '../../../components/styledComponents/StatusAlert';
import colors from '../../../constants/colors';
import { StatusType } from '../../../constants/enums';
import { SelectedPayment } from '../../../store/paymentsReducer';
import { policyTypeMap } from '../../../utils/policyTypeUtil';
import CardForm from './CardForm';

interface PaymentCardProps {
  bill: BillingDetails;
  cardIndex: number;
  paymentAmount: SelectedPayment;
  scheduledPayment?: SchedulePayment;
}

const PaymentCard = ({
  bill,
  cardIndex,
  paymentAmount,
  scheduledPayment,
}: PaymentCardProps) => {
  const { policyNumber, mortgageeBilled, autoPay, pastDue } = bill;
  const { data: policiesData } = usePoliciesQuery();
  const currentPolicy = policiesData?.policies?.find(
    (policy) => policy.policyNumber === policyNumber
  );
  const policyDetails = currentPolicy?.policyDetails;
  const policyCancelled = currentPolicy?.policyStatus
    .toLowerCase()
    .includes('cancel');
  const policyExpired = currentPolicy?.policyStatus === 'Expired';
  const isNanoPolicy = currentPolicy?.nanoPolicy;

  const cardHeaderText = `${
    policyTypeMap?.[bill?.fullProductCode]?.title
  } policy ${policyNumber.toUpperCase()}`;

  const AutoPayMessage = () => {
    if (autoPay.enrolled && autoPay.dueDate) {
      return (
        <Stack direction="row" spacing={1} sx={{
          alignItems: "center"
        }}>
          <AutoPayIcon />
          <Typography
            sx={{
              fontSize: { mobile: 16, tablet: 18 },
              fontWeight: 400,
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              color: colors.csaaBlack,
            }}
          >
            <Link to="/autopay">AutoPay</Link> scheduled for your due date
          </Typography>
        </Stack>
      );
    } else if (autoPay.eligible) {
      return (
        <Stack direction="row" spacing={1} sx={{
          alignItems: "center"
        }}>
          <AutoPayIcon />
          <Typography
            sx={{
              fontSize: { mobile: 16, tablet: 18 },
              fontWeight: 400,
              fontFamily: 'Cabin',
              fontKerning: 'normal',
              color: colors.csaaBlack,
            }}
          >
            Enroll in <Link to="/autopay">AutoPay</Link>
          </Typography>
        </Stack>
      );
    }

    return null;
  };

  return (
    <Card
      spacing={{ mobile: 2, tablet: 3 }}
      component={Stack}
      sx={{
        background: '#FFFFFF',
        borderRadius: 2,
        px: {
          mobile: 2,
          tablet: 2,
          desktop: 5,
        },
        py: {
          mobile: 2,
          tablet: 2,
          desktop: 3,
        },
        height: 'auto',
      }}
    >
      <CardHeader
        cancelled={policyCancelled}
        expired={policyExpired}
        headerText={cardHeaderText}
        policyTypeCd={bill.policyTypeCd}
        vehicles={policyDetails?.vehicles}
        insuredAddress={policyDetails?.insuredAddress}
        mortgageeBilled={mortgageeBilled}
        pastDue={pastDue}
        isNanoPolicy={isNanoPolicy}
        policyNumber={policyNumber}
        fullProductCode={bill.fullProductCode}
      />
      <StatusAlert
        open={
          !!(
            scheduledPayment?.schedulePaymentDetails?.paymentAmount &&
            scheduledPayment?.schedulePaymentDetails?.paymentDate
          )
        }
        text={`There is already a one-time payment of ${scheduledPayment?.schedulePaymentDetails?.paymentAmount} scheduled for ${scheduledPayment?.schedulePaymentDetails?.paymentDate}.`}
        status={StatusType.WARNING}
      />

      {paymentAmount && (
        <CardForm
          bill={bill}
          paymentAmount={paymentAmount}
          cardIndex={cardIndex}
        />
      )}
      {(autoPay.enrolled || autoPay.eligible) && <AutoPayMessage />}
    </Card>
  );
};

export default PaymentCard;
